import { showInfoNotification, showSuccessNotification, showWarningNotification } from '../../../../widgets/notificationToaster';
import React, { Suspense, useEffect } from 'react';
import { useLazyLoadQuery, useMutation, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { ErrorPage } from '../../../_error';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import Loader from 'src/components/widgets/loader';
import { FullscreenLayout } from 'src/components/widgets/fullscreenIframeLayout';
import { useSaveProduct } from 'src/components/widgets/preview3D/hooks/useSaveProduct';
import usePreview3DStore, { Preview3DStoreContext } from 'src/components/widgets/preview3D/store';
import { useSubscriptionData } from '../../../../../shared/hooks/useSubscriptionData';
import { useElementSize } from '../../../../../shared/hooks/useElementSize';
import { withTimeout } from 'src/shared/withTimeout';
import styled from 'styled-components';
import { stringFormat, T, toGlobalID, toLocalID } from '../../../../../shared/helpers';
import { productLogicName } from '../../../../../shared/productNames';
import { useProductConfigurationState } from '../../state';
import ComposerAttributeContentPage from '../configurator-components/composer-editors/attributes/composerAttributePage';
import {
    ComposerAreaNode,
    ComposerAttributeNode,
    ComposerAttributeType,
    ComposerEcommercePlatform
} from '../configurator-components/composer-editors/attributes/composerAttributesModels';
import AttributesEditor from '../configurator-components/composer-editors/attributesEditor';
import { ComposerResizeHandle } from '../configurator-components/composer-editors/composerWorkspace';
import { EditorLoadingCard } from '../configurator-components/editorLoadingCard';
import HeaderToolbar from '../configurator-components/headerToolbar';
import { useEscapeClosesModals } from '../../../../widgets/hooks/useEscapeToClose';
import SceneEditorSettingsDialog from '../configurator-components/sceneEditorSettingsDialog';
import { WORKSPACE_PANEL_CONTENT_MAX, WORKSPACE_PANEL_CONTENT_MIN, useWorkspacePreferences } from '../configurator-scene-editor/useWorkspacePreferences';
import { Dialog, useDialogManager } from '../../../../widgets/dialog';
import { composerQueriesEcommerceSettingsQuery } from './composer/__generated__/composerQueriesEcommerceSettingsQuery.graphql';
import { composerQueriesFontQuery } from './composer/__generated__/composerQueriesFontQuery.graphql';
import { composerQueriesListTemplatesQuery } from './composer/__generated__/composerQueriesListTemplatesQuery.graphql';
import { composerQueriesRefreshDesignQuery } from './composer/__generated__/composerQueriesRefreshDesignQuery.graphql';
import {
    composerEcommerceSettingsQuery,
    composerExtensionFieldsQuery,
    composerFontQuery,
    composerListTemplatesQuery,
    composerRefreshDesignQuery,
    composerMerchantOptionsQuery,
    composerPrintTypesQuery
} from './composer/composerQueries';
import useComposerStore from './composer/composerStore';
import { applyDesignPreviews } from './composer/helpers/composerDesignPreview';
import { getComposerBlockingValidationMessages, isPrintableComposerMesh } from './composer/helpers/composerAttributeValidation';
import {
    createComposerDirtySnapshot,
    hasComposerAreaChangesRequiredByFullEditor,
    hasComposerChangesOutsidePreset
} from './composer/helpers/composerDirtySnapshot';
import { mergeComposerTemplateAreaDraft } from './composer/helpers/mergeComposerTemplateAreaDraft';
import {
    clearComposerLocalDraft,
    hydrateComposerLocalDraft,
    isComposerLocalDraftBasedOn,
    loadComposerLocalDraft,
    saveComposerLocalDraft
} from './composer/helpers/composerLocalDraft';
import { ComposerAttributeNodeAdapter, ComposerTemplateNodeAdapter } from './composer/helpers/composerNodesAdapter';
import useSaveDesign from './composer/helpers/useSaveDesign';
import { composerDesignFingerprint } from './composer/helpers/reconcileComposerDesign';
import { getComposerDefaultTemplate } from './composer/helpers/composerDefaultTemplate';
import { initializeComposerDesignPrintingMethod } from './composer/helpers/composerDesignPrintingMethod';
import { setTemplateAsDefaultMutation } from '../../../productTemplates/designTemplatesQueries';
import { designTemplatesQueriesSetTemplateAsDefaultMutation } from '../../../productTemplates/__generated__/designTemplatesQueriesSetTemplateAsDefaultMutation.graphql';
import TemplateEditorDialog from 'src/components/pages/productTemplates/dialogs/templateEditorDialog';

const Container = styled(FullscreenLayout)`
    --w-panel-min: 350px;
    --composer-structure-min: var(--w-panel-min);

    /* The palette mirrors the 3D Scene Editor panel tokens (widgets/propertiesPanel/panelTheme),
       which take their values from the app theme, so both editors read as the same surface. */
    --composer-app: #f0f0f0;
    --composer-panel: #fff;
    --composer-sunken: #f9f9f9;
    --composer-hover: #eceff1;
    --composer-line: #dbe2e6;
    --composer-line-soft: #eef1f3;
    --composer-fg: #263238;
    --composer-muted: #455a64;
    --composer-faint: #78909c;
    --composer-accent: #e8650a;
    --composer-accent-deep: #c25309;
    --composer-accent-text: #a8480b;
    --composer-accent-wash: rgba(232, 101, 10, 0.08);
    --composer-kind-group: #7040d0;
    --composer-kind-attribute: #0e7c7c;
    --composer-kind-option: #b32d7d;
    /* The warning family the validation tooltip and the notes paint with. */
    --composer-warn-ink: #874b0b;
    --composer-warn-icon: #a85a08;
    --composer-warn-line: #f0dcc5;
    --composer-warn-line-strong: #f2c98d;
    --composer-warn-wash: #fff0df;
    --composer-warn-surface: #fff8ed;
    --composer-ai: #6b36ce;
    --composer-ai-wash: #f7f3fe;
    --composer-shadow: rgba(38, 50, 56, 0.04) 0 1px 2px;
    --c1: 2fr;
    --c2: 3fr;
    --c3: 5fr;
    --c23: 8fr;

    color: var(--composer-fg);
    background: var(--composer-app);
    font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        system-ui,
        sans-serif;
    font-size: 13px;
    z-index: 30;

    &,
    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    & > * {
        width: 100%;
    }
`;

const MainContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
    padding: 0 6px 6px;
    box-sizing: border-box;
`;

const MainContentLayout = styled.div<{ $structureWidth: number }>`
    display: grid;
    grid-template-columns: ${(props) => `${props.$structureWidth}px`} 6px minmax(0, var(--c23));
    gap: 0;
    height: 100%;
    max-height: 100%;
    background: var(--composer-app);

    @media (max-width: 940px) {
        grid-template-columns: 1fr;

        > :first-child,
        > [role='separator'] {
            display: none;
        }
    }
`;

const PreviewContainer = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

// La card dell'attesa se n'è andata in `configurator-components/editorLoadingCard`: la stessa la
// mostra lo Scene Editor, e i due ambienti devono aspettare con la stessa faccia.

const COMPOSER_SAVE_TIMEOUT_MS = 120_000;
const COMPOSER_REQUIRED_DATA_TIMEOUT_MS = 45_000;

const collectErrorMessages = (value: unknown, seen = new Set<object>()): string[] => {
    if (typeof value === 'string') return value.trim() ? [value] : [];
    if (value instanceof Error) return value.message ? [value.message] : [];
    if (Array.isArray(value)) return value.flatMap((item) => collectErrorMessages(item, seen));
    if (!value || typeof value !== 'object' || seen.has(value)) return [];

    seen.add(value);
    const record = value as Record<string, unknown>;
    return ['message', 'reason', 'errors', 'source', 'cause', 'networkError', 'extensions'].flatMap((key) => collectErrorMessages(record[key], seen));
};

const asError = (error: unknown): Error => {
    if (error instanceof Error) return error;
    const messages = [...new Set(collectErrorMessages(error))];
    return new Error(messages.join(' · ') || stringFormat(T._('An unexpected error occurred while saving {0}.', 'Admin'), productLogicName()));
};

const uniqueComposerDesigns = (designs: Map<string, MPlaza.Design>): MPlaza.Design[] => {
    const byTemplate = new Map<string, MPlaza.Design>();
    for (const [key, design] of designs) {
        const designID = Number(design.getDesignID() || 0);
        const separator = key.indexOf(',');
        const templateKey = separator < 0 ? key : key.slice(0, separator);
        // Multiple area keys reference one product template. Object identity is not
        // stable after localStorage hydration, so it cannot be the save identity.
        byTemplate.set(designID > 0 ? `saved:${designID}` : `draft:${templateKey}`, design);
    }
    return Array.from(byTemplate.values());
};

const reconcileComposerAttributeNodes = (nodes: ComposerAttributeNode[], product: MPlaza.Model) => {
    const findBy = (collection: any, key: string, value: unknown) => collection?.find?.((item: any) => item.get?.(key) === value);
    for (const node of nodes) {
        if (node.type === ComposerAttributeType.PreSetContainer) {
            const preset = findBy(product.get('templateGroups'), 'templateGroupGuid', node.id);
            if (preset) {
                node.idNumeric = preset.get('templateGroupID') ?? node.idNumeric;
                node.isNew = false;
                node.data = preset.toJSON();
            }
            continue;
        }
        const collection =
            node.type === ComposerAttributeType.Attribute
                ? product.get('attributes')
                : node.type === ComposerAttributeType.AttributeGroup
                  ? product.get('attributeGroups')
                  : product.get('attributeSteps');
        const guidKey =
            node.type === ComposerAttributeType.Attribute ? 'attributeGuid' : node.type === ComposerAttributeType.AttributeGroup ? 'groupGuid' : 'stepGuid';
        const idKey = node.type === ComposerAttributeType.Attribute ? 'attributeID' : node.type === ComposerAttributeType.AttributeGroup ? 'groupID' : 'stepID';
        const model = findBy(collection, guidKey, node.id);
        if (!model) continue;

        node.idNumeric = model.get(idKey) ?? node.idNumeric;
        node.isNew = false;
        node.data = model.toJSON();
        if (node.type !== ComposerAttributeType.Attribute) {
            if (node.type === ComposerAttributeType.AttributeGroup) node.img = model.get('imageUrl') || '';
            continue;
        }

        for (const optionNode of node.options) {
            const option = findBy(model.get('options'), 'optionGuid', optionNode.id);
            if (!option) continue;
            optionNode.idNumeric = option.get('optionID') ?? optionNode.idNumeric;
            optionNode.isNew = false;
            optionNode.img = option.get('imageUrl') || '';
            optionNode.data = option.toJSON();
            for (const actionNode of optionNode.actions) {
                const action = findBy(option.get('actions'), 'actionGuid', actionNode.id);
                if (action) {
                    actionNode.isNew = false;
                    actionNode.data = action.toJSON();
                }
            }
        }
        for (const pricingNode of node.pricings) {
            const pricing = model
                .get('pricings')
                ?.find?.((item: any) =>
                    pricingNode.selectedOptionGuid
                        ? item.get?.('selectedOptionGuid') === pricingNode.selectedOptionGuid
                        : item.get?.('selectedValue') === pricingNode.selectedValue && item.get?.('pricingID') === pricingNode.pricingID
                );
            if (pricing) pricingNode.attributePricingID = pricing.get?.('attributePricingID') ?? pricing.attributePricingID;
        }
    }
};

type TemplateRefreshTarget = { request: number; templateNodeID: string; designID: number };

const RefreshedTemplate: React.FC<{
    target: TemplateRefreshTarget;
    onLoaded: (data: composerQueriesRefreshDesignQuery['response'], target: TemplateRefreshTarget) => void;
}> = ({ target, onLoaded }) => {
    // The editor saves outside Relay. Refetch only its design; the product and 3D scene stay mounted.
    const data = useLazyLoadQuery<composerQueriesRefreshDesignQuery>(
        composerRefreshDesignQuery,
        { designId: toGlobalID(target.designID, 'Design') },
        { fetchPolicy: 'network-only', fetchKey: target.request }
    );
    useEffect(() => onLoaded(data, target), [data, target, onLoaded]);
    return null;
};

const TemplateRefreshFailed: React.FC<{ onFailure: () => void }> = ({ onFailure }) => {
    useEffect(() => {
        onFailure();
        showWarningNotification({
            message: stringFormat(
                T._('The template was saved, but its latest data could not be loaded. Reload {0} before editing it again.', 'Admin'),
                productLogicName()
            )
        });
    }, [onFailure]);
    return null;
};

const Composer = () => {
    // Esc chiude la finestra in primo piano, finché si lavora qui: vedi `useEscapeToClose`.
    useEscapeClosesModals();
    const { state, unsavedChanges } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const preview3DStore = React.useContext(Preview3DStoreContext);
    const { subscription } = useSubscriptionData();
    const additionalFeatures = subscription?.plan?.additionalFeatures;
    const workspaceRef = React.useRef<HTMLDivElement>(null);
    const [measureWorkspace, workspaceSize] = useElementSize(workspaceRef);
    const { preferences, setPreference, setPanelSize, resetPanelSizes, snapshotPreferences, restorePreferences } = useWorkspacePreferences(workspaceSize);
    // Product logic has one structure only. Areas are children of their attributes, templates open
    // in their editor, and printing methods are managed by the Backoffice. A one-entry rail would
    // only spend width without offering navigation, so this column uses the content limits directly.
    const structureMinWidth = WORKSPACE_PANEL_CONTENT_MIN;
    const structureMaxWidth = workspaceSize?.width && workspaceSize.width > 2048 ? 600 : WORKSPACE_PANEL_CONTENT_MAX;
    const [zkProduct, zkScene, sceneEditor, setZkProduct] = usePreview3DStore((x) => [x.zkProduct, x.zkScene, x.sceneEditor, x.setZkProduct]);
    const [saveProduct] = useSaveProduct();
    const [saveDesign] = useSaveDesign();
    const [setTemplateAsDefault] = useMutation<designTemplatesQueriesSetTemplateAsDefaultMutation>(setTemplateAsDefaultMutation);
    const relayEnvironment = useRelayEnvironment();
    const fetchedEcommerceSettings = useLazyLoadQuery<composerQueriesEcommerceSettingsQuery>(
        composerEcommerceSettingsQuery,
        {},
        {
            fetchPolicy: 'store-or-network'
        }
    );

    const fetchedTemplates = useLazyLoadQuery<composerQueriesListTemplatesQuery>(
        composerListTemplatesQuery,
        {
            productId: toLocalID(state.product.id) ?? 0
        },
        {
            fetchPolicy: 'store-or-network'
        }
    );

    const queryFont = useLazyLoadQuery<composerQueriesFontQuery>(
        composerFontQuery,
        {},
        {
            fetchPolicy: 'store-or-network'
        }
    );

    const [
        storedAttributes,
        storedTemplates,
        storedAreas,
        storedDesigns,
        setModel,
        setFonts,
        initializeStoreAttributeInfo,
        initializeEcommerceSettings,
        initializeStoreTemplateInfo,
        setDesigns,
        setProductQuantity,
        setSceneMaterials,
        setSceneObjects,
        setSceneCameras,
        setRequiredEditorData,
        validateAttributes
    ] = useComposerStore((x) => [
        x.attributes,
        x.templates,
        x.areas,
        x.designs,
        x.setModel,
        x.setFonts,
        x.intializeAttributesInfo,
        x.setEcommerceSettings,
        x.initializeTemplateInfo,
        x.setDesigns,
        x.setProductQuantity,
        x.setSceneMaterialList,
        x.setSceneObjectList,
        x.setSceneCameraList,
        x.setRequiredEditorData,
        x.validateAttributes
    ]);

    const [structureWidth, setStructureWidth] = React.useState<number>();
    const resolvedStructureWidth = structureWidth ?? Math.min(Math.max(preferences.sizes.left, structureMinWidth), structureMaxWidth);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [requiredEditorDataReady, setRequiredEditorDataReady] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const [fullEditorDirty, setFullEditorDirty] = React.useState(false);
    const [localDraftState, setLocalDraftState] = React.useState<'none' | 'saving' | 'saved' | 'failed'>('none');
    const [templateRefreshTarget, setTemplateRefreshTarget] = React.useState<TemplateRefreshTarget | null>(null);
    const [templateRefreshFailed, setTemplateRefreshFailed] = React.useState(false);
    const [savingMessage, setSavingMessage] = React.useState<string>(stringFormat(T._('Preparing to save {0}...', 'Admin'), productLogicName()));
    const [saveError, setSaveError] = React.useState<Error | null>(null);
    const composerReadyRef = React.useRef(false);
    const composerSnapshotRef = React.useRef('');
    const savedDesignFingerprintsRef = React.useRef(new Map<number, string>());
    const saveInFlightRef = React.useRef<Promise<boolean> | null>(null);
    const cancelPendingLocalDraftRef = React.useRef<() => void>(() => undefined);
    const initializedProductRef = React.useRef<MPlaza.Model | null>(null);
    const sharedMeshSideSupportRef = React.useRef(false);

    // A server-side preference update, a rail change, or a workspace resize may resolve the
    // persisted fraction to another pixel width. Once that happens the shared preference is the
    // source of truth again; the local value only exists while the pointer is moving.
    useEffect(() => setStructureWidth(undefined), [preferences.sizes.left]);

    useEffect(() => {
        let disposed = false;
        composerReadyRef.current = false;
        setIsLoading(true);
        setRequiredEditorDataReady(false);

        const loadRequiredEditorData = async () => {
            try {
                const [merchantOptionsResult, printTypesResult, extensionFieldsResult] = await withTimeout(
                    Promise.allSettled([
                        fetchQuery<any>(
                            relayEnvironment,
                            composerMerchantOptionsQuery,
                            { salesChannelId: state.salesChannelID, sku: state.product.code },
                            { fetchPolicy: 'store-or-network' }
                        ).toPromise(),
                        fetchQuery<any>(relayEnvironment, composerPrintTypesQuery, {}, { fetchPolicy: 'store-or-network' }).toPromise(),
                        fetchQuery<any>(relayEnvironment, composerExtensionFieldsQuery, {}, { fetchPolicy: 'store-or-network' }).toPromise()
                    ]),
                    COMPOSER_REQUIRED_DATA_TIMEOUT_MS,
                    stringFormat(T._('{0} support data took too long to load.', 'Admin'), productLogicName())
                );

                if (disposed) return;

                const merchantProductOptions =
                    merchantOptionsResult.status === 'fulfilled'
                        ? (merchantOptionsResult.value?.merchantProductOptions ?? []).flatMap((option: any) =>
                              option?.optionID == null
                                  ? []
                                  : [
                                        {
                                            optionID: String(option.optionID),
                                            label: option.label || String(option.optionID),
                                            values: (option.values ?? []).flatMap((value: any) =>
                                                value?.valueID == null ? [] : [{ valueID: String(value.valueID), label: value.label || String(value.valueID) }]
                                            )
                                        }
                                    ]
                          )
                        : [];
                const merchantProductOptionsError =
                    merchantOptionsResult.status === 'rejected' || merchantOptionsResult.value == null
                        ? stringFormat(
                              T._('Store attributes could not be loaded. Local attributes remain available; retry by reloading {0}.', 'Admin'),
                              productLogicName()
                          )
                        : null;
                const printTypes =
                    printTypesResult.status === 'fulfilled'
                        ? (printTypesResult.value?.printTypes?.edges ?? []).flatMap((edge: any) =>
                              edge?.node?.printTypeID != null
                                  ? [{ printTypeID: Number(edge.node.printTypeID), name: edge.node.name || String(edge.node.printTypeID) }]
                                  : []
                          )
                        : [];
                const extensionFieldDefinitions =
                    extensionFieldsResult.status === 'fulfilled'
                        ? (extensionFieldsResult.value?.extensionFields ?? []).flatMap((field: any) =>
                              field?.fieldID != null
                                  ? [
                                        {
                                            id: field.id,
                                            fieldID: Number(field.fieldID),
                                            name: field.name || '',
                                            label: field.label || '',
                                            value: '',
                                            dataTypeID: field.dataTypeID ?? 0,
                                            dataTypeName: field.dataTypeName,
                                            operationTypeID: field.operationTypeID ?? 0,
                                            operationTypeName: field.operationTypeName,
                                            formatString: field.formatString || ''
                                        }
                                    ]
                                  : []
                          )
                        : [];

                setRequiredEditorData({ merchantProductOptions, merchantProductOptionsError, printTypes, extensionFieldDefinitions });
            } catch {
                if (disposed) return;
                setRequiredEditorData({
                    merchantProductOptions: [],
                    merchantProductOptionsError: stringFormat(
                        T._('Store attributes could not be loaded. Local attributes remain available; retry by reloading {0}.', 'Admin'),
                        productLogicName()
                    ),
                    printTypes: [],
                    extensionFieldDefinitions: []
                });
            } finally {
                if (!disposed) setRequiredEditorDataReady(true);
            }
        };

        void loadRequiredEditorData();
        return () => {
            disposed = true;
        };
    }, [relayEnvironment, setRequiredEditorData, state.product.code, state.salesChannelID]);

    useEffect(() => {
        const productID = String(toLocalID(state.product.id) ?? '');
        if (!productID) return undefined;

        let pendingSnapshot: string | null = null;
        let draftTimer: number | undefined;
        const persistPendingDraft = (updateState = true) => {
            if (draftTimer !== undefined) window.clearTimeout(draftTimer);
            draftTimer = undefined;
            if (!pendingSnapshot) return;
            const snapshot = pendingSnapshot;
            pendingSnapshot = null;
            const persisted = saveComposerLocalDraft(productID, composerSnapshotRef.current, snapshot);
            if (updateState) setLocalDraftState(persisted ? 'saved' : 'failed');
        };
        const queueDraft = (snapshot: string) => {
            pendingSnapshot = snapshot;
            setLocalDraftState('saving');
            if (draftTimer !== undefined) window.clearTimeout(draftTimer);
            draftTimer = window.setTimeout(() => persistPendingDraft(), 350);
        };
        const cancelPendingDraft = () => {
            pendingSnapshot = null;
            if (draftTimer !== undefined) window.clearTimeout(draftTimer);
            draftTimer = undefined;
        };
        cancelPendingLocalDraftRef.current = cancelPendingDraft;
        const unsubscribe = useComposerStore.subscribe((current, previous) => {
            if (!composerReadyRef.current) return;
            if (
                current.attributes !== previous.attributes ||
                current.templates !== previous.templates ||
                current.areas !== previous.areas ||
                current.designs !== previous.designs ||
                current.productQuantity !== previous.productQuantity ||
                current.ecommerceSettings !== previous.ecommerceSettings
            ) {
                const currentSnapshot = createComposerDirtySnapshot(current);
                const isDirty = currentSnapshot !== composerSnapshotRef.current;
                setHasUnsavedChanges(isDirty);
                if (isDirty) {
                    queueDraft(currentSnapshot);
                } else {
                    cancelPendingDraft();
                    clearComposerLocalDraft(productID);
                    setLocalDraftState('none');
                }
            }
        });
        const flushDraft = () => persistPendingDraft(false);
        window.addEventListener('pagehide', flushDraft);

        return () => {
            window.removeEventListener('pagehide', flushDraft);
            persistPendingDraft(false);
            cancelPendingLocalDraftRef.current = () => undefined;
            unsubscribe();
        };
    }, [state.product.id]);

    useEffect(() => {
        if (!zkProduct || !zkScene || !fetchedTemplates || !queryFont || !requiredEditorDataReady) return;
        // Refetching a template or remounting the preview after a save error must not
        // hydrate an older local draft over IDs just assigned by ProductService.
        // Scene/viewer data is refreshed separately; only a new product initializes
        // the Composer nodes.
        if (initializedProductRef.current === zkProduct && useComposerStore.getState().model === zkProduct) {
            composerReadyRef.current = true;
            setIsLoading(false);
            return;
        }

        composerReadyRef.current = false;
        // This must come from the last server payload, not from locally generated
        // mesh-side keys: an older ProductService would silently replace the first area.
        sharedMeshSideSupportRef.current =
            zkProduct
                .get('scenes')
                .at(0)
                ?.get('meshSides')
                ?.models.some((meshSide) => /^.+_\d+_-?\d+$/.test(meshSide.get('key') || '')) ?? false;

        setModel(zkProduct);

        setFonts(queryFont);

        const ecommerceSettingsData = fetchedEcommerceSettings?.ecommerceSettings;
        initializeEcommerceSettings({
            ecommercePlatform: ecommerceSettingsData?.ecommerceID ?? (-1 as ComposerEcommercePlatform)
        });

        const modelAttributes = zkProduct.get('attributes') || [];
        const attributes = modelAttributes.map(ComposerAttributeNodeAdapter.toNode);
        const steps = (zkProduct.get('attributeSteps') || []).map(ComposerAttributeNodeAdapter.toStepNode);
        const groups = (zkProduct.get('attributeGroups') || []).map(ComposerAttributeNodeAdapter.toGroupNode);
        const presets = (zkProduct.get('templateGroups') || []).map(ComposerAttributeNodeAdapter.toPresetNode);

        // The API already persists displayOrder. Re-numbering the combined attributes/steps/groups
        // collection here used to reverse the tree at every reload and also mixed unrelated sibling
        // scopes. Keep the server order intact; moves normalize only the siblings they actually touch.
        const composerAttributes = [ComposerAttributeNodeAdapter.adjustValues(attributes), steps, groups, presets].flat();
        initializeStoreAttributeInfo(modelAttributes, composerAttributes);

        const seenTemplateIDs = new Set<string>();
        const templates =
            fetchedTemplates?.designTemplates
                ?.filter((template) => {
                    if (seenTemplateIDs.has(template.id)) return false;
                    seenTemplateIDs.add(template.id);
                    return true;
                })
                .map((te) =>
                    ComposerTemplateNodeAdapter.toNode(toLocalID(te.id) ?? 0, te.name, te.design, null, zkProduct, zkProduct.get('colors').at(0)?.get('sides'))
                ) || [];
        const seenSideIDs = new Set<number>();
        const areas =
            zkProduct
                .get('colors')
                .at(0)
                ?.get('sides')
                .filter((side) => {
                    const sideID = side.get('sideID');
                    if (seenSideIDs.has(sideID)) return false;
                    seenSideIDs.add(sideID);
                    return true;
                })
                .map((side) => ComposerTemplateNodeAdapter.toNodeArea(side, zkProduct.get('scenes').at(0).get('meshSides'))) || [];
        areas.forEach((area) => {
            area.attributeGuid = composerAttributes.find(
                (attribute) => attribute.type === ComposerAttributeType.Attribute && attribute.idNumeric === area.attributeID
            )?.id;
        });
        initializeStoreTemplateInfo(templates, areas);
        savedDesignFingerprintsRef.current = new Map(
            uniqueComposerDesigns(useComposerStore.getState().designs).map((design) => [Number(design.getDesignID()), composerDesignFingerprint(design)])
        );
        const serverSnapshot = createComposerDirtySnapshot(useComposerStore.getState());
        composerSnapshotRef.current = serverSnapshot;

        const productID = String(toLocalID(state.product.id) ?? '');
        const storedDraft = productID ? loadComposerLocalDraft(productID) : null;
        const hydratedDraft = storedDraft && storedDraft.snapshot !== serverSnapshot ? hydrateComposerLocalDraft(storedDraft.snapshot) : null;
        if (storedDraft && hydratedDraft) {
            initializeStoreAttributeInfo(modelAttributes, hydratedDraft.attributes);
            initializeStoreTemplateInfo(hydratedDraft.templates, hydratedDraft.areas);
            setDesigns(hydratedDraft.designs);
            setProductQuantity(hydratedDraft.productQuantity);
            if (hydratedDraft.ecommerceSettings) initializeEcommerceSettings(hydratedDraft.ecommerceSettings);
            setLocalDraftState('saved');
            const message = isComposerLocalDraftBasedOn(storedDraft.draft, serverSnapshot)
                ? stringFormat(T._('Your local {0} draft was restored. It is still not saved to the product.', 'Admin'), productLogicName())
                : stringFormat(
                      T._('A local {0} draft was restored over a newer server version. Review it before saving the product.', 'Admin'),
                      productLogicName()
                  );
            if (isComposerLocalDraftBasedOn(storedDraft.draft, serverSnapshot)) showInfoNotification({ message });
            else showWarningNotification({ message });
        } else if (productID && storedDraft) {
            clearComposerLocalDraft(productID);
            setLocalDraftState('none');
        }
        initializedProductRef.current = zkProduct;
        void validateAttributes().finally(() => {
            unsavedChanges.resetSnapshot();
            composerReadyRef.current = true;
            setHasUnsavedChanges(createComposerDirtySnapshot(useComposerStore.getState()) !== composerSnapshotRef.current);
            setIsLoading(false);
        });
    }, [zkProduct, zkScene, fetchedTemplates, queryFont, fetchedEcommerceSettings, requiredEditorDataReady]);

    useEffect(() => {
        if (!sceneEditor || !sceneEditor?.scene?.meshes) return;

        setSceneObjects(sceneEditor!.scene!.meshes);
        setSceneMaterials(sceneEditor!.scene!.materials);
        setSceneCameras(sceneEditor!.scene!.cameras);
        void validateAttributes();
    }, [sceneEditor]);

    // Hooks must run during both the loading and ready renders. Defining these after the
    // loading return caused React error #310 as soon as the Composer finished loading.
    const onTemplateRefreshFailed = React.useCallback(() => setTemplateRefreshFailed(true), []);

    const onRefreshedTemplate = React.useCallback(
        (data: composerQueriesRefreshDesignQuery['response'], target: TemplateRefreshTarget) => {
            const saved = data.design;
            const current = useComposerStore.getState().templates.find((candidate) => candidate.id === target.templateNodeID);
            if (!saved || saved.designID !== target.designID || !current || !zkProduct) {
                setTemplateRefreshFailed(true);
                showWarningNotification({
                    message: stringFormat(
                        T._('The template was saved, but its latest data could not be found. Reload {0} before editing it again.', 'Admin'),
                        productLogicName()
                    )
                });
                setTemplateRefreshTarget(null);
                return;
            }

            const refreshed = ComposerTemplateNodeAdapter.toNode(
                current.templateID,
                current.name,
                saved,
                current.id,
                zkProduct,
                zkProduct.get('colors').at(0)?.get('sides')
            );
            composerReadyRef.current = false;
            useComposerStore.setState((state) => {
                const designs = new Map(state.designs);
                for (const key of designs.keys()) if (key.startsWith(`${current.id},`)) designs.set(key, refreshed.design);
                return {
                    templates: state.templates.map((template) => (template.id === current.id ? refreshed : template)),
                    selectedTemplates: state.selectedTemplates?.map((template) => (template.id === current.id ? refreshed : template)) ?? null,
                    designs
                };
            });
            composerReadyRef.current = true;
            const currentSnapshot = createComposerDirtySnapshot(useComposerStore.getState());
            setHasUnsavedChanges(currentSnapshot !== composerSnapshotRef.current);
            const productID = String(toLocalID(state.product.id) ?? '');
            if (productID && currentSnapshot !== composerSnapshotRef.current) {
                setLocalDraftState(saveComposerLocalDraft(productID, composerSnapshotRef.current, currentSnapshot) ? 'saved' : 'failed');
            }
            setTemplateRefreshTarget(null);
            showSuccessNotification({ message: T._('Template saved successfully', 'Admin') });
        },
        [zkProduct, state.product.id]
    );

    if (isLoading) {
        return (
            <Container>
                <EditorLoadingCard
                    title={stringFormat(T._('Loading {0}', 'Admin'), productLogicName())}
                    lines={[T._('We are loading the product structure and the 3D scene. This may take a few moments.', 'Admin')]}
                />
            </Container>
        );
    }

    const performSave = async () => {
        console.log('Save composer');
        if (!zkProduct || !storedAttributes) return false;
        setSaveError(null);
        const validatedAttributes = await validateAttributes();
        const validatedAreas = useComposerStore.getState().areas;
        const meshAreaOwners = new Map<string, Set<string>>();
        validatedAreas.forEach((area) => {
            new Set((area.objects || '').split(';').filter(Boolean)).forEach((meshID) => {
                const owners = meshAreaOwners.get(meshID) ?? new Set<string>();
                owners.add(area.id);
                meshAreaOwners.set(meshID, owners);
            });
        });
        const hasSharedMesh = Array.from(meshAreaOwners.values()).some((owners) => owners.size > 1);
        if (hasSharedMesh && !sharedMeshSideSupportRef.current) {
            showWarningNotification({
                message: stringFormat(
                    T._(
                        'Shared meshes are selected, but ProductService has not confirmed support for multiple areas on the same mesh. {0} was not saved to avoid losing an area.',
                        'Admin'
                    ),
                    productLogicName()
                )
            });
            return false;
        }
        const validationMessages = getComposerBlockingValidationMessages(validatedAttributes, validatedAreas);
        if (validationMessages.length > 0) {
            const preview = validationMessages
                .slice(0, 3)
                .map((message, index) => `${index + 1}. ${message}`)
                .join(' ');
            const remaining = validationMessages.length > 3 ? ` ${validationMessages.length - 3} more configuration issue(s).` : '';
            showWarningNotification({
                message: `${stringFormat(T._('Saving is blocked. Fix {0} validation errors shown by the warning icons.', 'Admin'), productLogicName())} ${preview}${remaining}`
            });
            return false;
        }

        setSavingMessage(T._('Saving product data...', 'Admin'));
        setIsSaving(true);
        try {
            // Generate previews before the first server mutation. A broken image/font must not
            // leave the product saved while its templates are still stale.
            const uniqueDesigns = uniqueComposerDesigns(storedDesigns);
            const designsToValidate = new Set([...uniqueDesigns, ...storedTemplates.map((template) => template.design)]);
            for (const design of designsToValidate) {
                if ((design as any).get('isDeleted')) continue;
                initializeComposerDesignPrintingMethod(design);
            }
            const dirtyDesigns = uniqueDesigns.filter(
                (design) => savedDesignFingerprintsRef.current.get(Number(design.getDesignID())) !== composerDesignFingerprint(design)
            );
            setSavingMessage(T._('Generating design previews...', 'Admin'));
            await withTimeout(
                applyDesignPreviews(
                    dirtyDesigns.filter((design) => !(design as any).get('isDeleted')),
                    storedAreas,
                    zkProduct
                ),
                COMPOSER_SAVE_TIMEOUT_MS,
                T._('Generating the design previews took too long. Please try again.', 'Admin')
            );

            setSavingMessage(T._('Saving product data...', 'Admin'));
            console.log('Attributes to process:', validatedAttributes);
            const { attributes, groups, steps, templateGroups } = ComposerAttributeNodeAdapter.saveAttributes(validatedAttributes);
            console.log('Attributes to save:', attributes, groups, steps);
            zkProduct.set('attributes', attributes);
            zkProduct.set('attributeGroups', groups);
            zkProduct.set('templateGroups', templateGroups);
            zkProduct.set('attributeSteps', steps);

            storedAreas.forEach((area) => {
                const attribute = validatedAttributes.find((candidate) => candidate.id === area.attributeGuid);
                if (attribute) area.attributeID = attribute.idNumeric;
            });
            const hasUnresolvedAreaLinks = storedAreas.some((area) => area.attributeGuid && (!area.attributeID || area.attributeID <= 0));

            const { sides, modelSceneMeshSides } = ComposerTemplateNodeAdapter.saveTemplates(storedTemplates, storedAreas);

            zkProduct.get('scenes').at(0).set('meshSides', modelSceneMeshSides);
            zkProduct.get('colors').at(0).set('sides', sides);

            setZkProduct(zkProduct);
            console.log('Saving product:', zkProduct);
            const temporarySideIDs = new Map(storedAreas.map((area) => [area.id, area.sideID]));
            await saveProduct({});

            // The server consumes temporary upload keys and assigns numeric IDs. Keep the
            // editor nodes in sync immediately so a later scene/design failure can be retried
            // without recreating attributes/options or resubmitting an already-consumed image.
            reconcileComposerAttributeNodes(validatedAttributes, zkProduct);
            for (const area of storedAreas) {
                area.sideID = area.originalSide.get('sideID');
                area.originalMeshSides?.forEach((meshSide) => meshSide.set('sideID', area.sideID));
                const attribute = validatedAttributes.find((candidate) => candidate.id === area.attributeGuid);
                if (attribute) area.attributeID = attribute.idNumeric;
            }
            // Validation can replace node objects while the product request is in flight.
            // Publish assigned IDs immediately, even if a later template save fails. A
            // retry must reuse this side rather than serialize its old temporary ID again.
            const savedAreasByID = new Map(storedAreas.map((area) => [area.id, area]));
            useComposerStore.setState((current) => {
                const reconcileArea = (area: ComposerAreaNode) => {
                    const saved = savedAreasByID.get(area.id);
                    return saved ? { ...area, sideID: saved.sideID, attributeID: saved.attributeID, originalSide: saved.originalSide } : area;
                };
                return {
                    areas: current.areas.map(reconcileArea),
                    selectedAreas: current.selectedAreas?.map(reconcileArea) ?? null
                };
            });
            const persistedSideIDs = new Map(
                storedAreas
                    .map((area) => [temporarySideIDs.get(area.id), area.sideID] as const)
                    .filter((entry): entry is readonly [number, number] => entry[0] !== undefined && entry[0] !== entry[1])
            );
            if (persistedSideIDs.size) {
                const savedAreasByTemporarySide = new Map(storedAreas.map((area) => [temporarySideIDs.get(area.id), area] as const));
                const designsToReconcile = new Set([...uniqueDesigns, ...storedTemplates.map((template) => template.design)]);
                for (const design of designsToReconcile) {
                    design.get('sides').forEach((side) => {
                        const persistedID = persistedSideIDs.get(side.get('sideID'));
                        if (persistedID !== undefined) side.set('sideID', persistedID);
                    });
                    design.get('designItems').forEach((item) =>
                        item.get('areas').forEach((itemArea) => {
                            const temporarySideID = itemArea.get('sideID');
                            const persistedID = persistedSideIDs.get(temporarySideID);
                            if (persistedID === undefined) return;
                            itemArea.set('sideID', persistedID);
                            const savedAreaID = savedAreasByTemporarySide.get(temporarySideID)?.originalSide.get('areas').at(0)?.get('areaID');
                            if (savedAreaID && savedAreaID > 0) itemArea.set('areaID', savedAreaID);
                        })
                    );
                }
                for (const template of storedTemplates) {
                    for (const [temporaryID, persistedID] of persistedSideIDs) {
                        const restriction = template.restrictions.get(temporaryID);
                        if (restriction) {
                            template.restrictions.delete(temporaryID);
                            template.restrictions.set(persistedID, restriction);
                        }
                    }
                }
            }

            // New attributes receive their database IDs only after the first mutation. MeshSide
            // deliberately stores AttributeID (not a GUID), so finish those new links in a second,
            // idempotent product update rather than sending invalid temporary negative IDs.
            if (hasUnresolvedAreaLinks) {
                const linkedTemplates = ComposerTemplateNodeAdapter.saveTemplates(storedTemplates, storedAreas);
                zkProduct.get('scenes').at(0).set('meshSides', linkedTemplates.modelSceneMeshSides);
                zkProduct.get('colors').at(0).set('sides', linkedTemplates.sides);
                await saveProduct({});
            }

            for (const [index, design] of dirtyDesigns.entries()) {
                setSavingMessage(`${T._('Saving designs...', 'Admin')} ${index + 1}/${dirtyDesigns.length}`);
                console.log('Saving design:', design);
                await withTimeout(saveDesign(design), COMPOSER_SAVE_TIMEOUT_MS, T._('Saving a design took too long. Please try again.', 'Admin'));
                savedDesignFingerprintsRef.current.set(Number(design.getDesignID()), composerDesignFingerprint(design));
            }
            // 2D attributes all edit one product default. Persist that choice through the
            // existing template API; otherwise the shopper loads a historical first template.
            const defaultTemplate = getComposerDefaultTemplate(storedTemplates, zkProduct.get('defaultDesignTemplateID'));
            const defaultDesignID = Number(defaultTemplate?.design.getDesignID() || 0);
            if (
                validatedAttributes.some((attribute) => attribute.attributeType === MPlaza.AttributeType.CustomizableArea) &&
                defaultDesignID > 0 &&
                Number(zkProduct.get('defaultDesignTemplateID')) !== defaultDesignID
            ) {
                const templateGlobalID = fetchedTemplates.designTemplates?.find((template) => template.design.designID === defaultDesignID)?.id;
                await withTimeout(
                    new Promise<void>((resolve, reject) => {
                        setTemplateAsDefault({
                            variables: {
                                modelId: Number(zkProduct.get('modelID')),
                                templateId: templateGlobalID || toGlobalID(defaultDesignID, 'DesignTemplate')
                            },
                            updater: (store) => {
                                store.get(toGlobalID(zkProduct.get('modelID'), 'Product'))?.setValue(defaultDesignID, 'defaultDesignTemplateID');
                            },
                            onCompleted: (_response, errors) =>
                                errors?.length ? reject(new Error(errors.map((error) => error.message).join(', '))) : resolve(),
                            onError: reject
                        });
                    }),
                    COMPOSER_SAVE_TIMEOUT_MS,
                    T._('Saving the default template took too long. Please try again.', 'Admin')
                );
                zkProduct.set('defaultDesignTemplateID', defaultDesignID);
            }
            // Adopt IDs in place; keep the 3D viewer, selection, and product cache mounted.
            useComposerStore.setState((current) => ({
                templates: current.templates.map((template) => ({
                    ...template,
                    templateID: Number(template.design.getDesignID()),
                    designDocID: (template.design as any).get('docID') || ''
                })),
                areas: [...current.areas],
                attributes: [...current.attributes]
            }));
            composerSnapshotRef.current = createComposerDirtySnapshot(useComposerStore.getState());
            setHasUnsavedChanges(false);
            cancelPendingLocalDraftRef.current();
            const productID = String(toLocalID(state.product.id) ?? '');
            if (productID) clearComposerLocalDraft(productID);
            setLocalDraftState('none');
            unsavedChanges.resetSnapshot();
            showSuccessNotification({
                message: stringFormat(T._('{0} settings saved successfully', 'Admin'), productLogicName())
            });
            return true;
        } catch (error) {
            console.error('Error during save:', error);
            setSaveError(asError(error));
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const onSave = (refreshProductAfterSave = true) => {
        if (templateRefreshFailed) {
            showWarningNotification({
                message: stringFormat(T._('Reload {0} before saving: the last template edit could not be synchronized.', 'Admin'), productLogicName())
            });
            return Promise.resolve(false);
        }
        // React state is not synchronous: two clicks in the same frame used to start two
        // create/update sequences. Coalesce them on the exact same promise instead.
        if (saveInFlightRef.current) return saveInFlightRef.current;
        const operation = performSave().finally(() => {
            if (saveInFlightRef.current === operation) saveInFlightRef.current = null;
        });
        saveInFlightRef.current = operation;
        return operation;
    };

    const openFullEditor = async (area: ComposerAreaNode, templateNodeID: string) => {
        if (templateRefreshFailed) {
            showWarningNotification({ message: stringFormat(T._('Reload {0} before editing this template again.', 'Admin'), productLogicName()) });
            return false;
        }
        const currentMeshIDs = [...new Set((area.objects || '').split(';').filter(Boolean))];
        if (currentMeshIDs.length === 0) {
            showWarningNotification({ message: T._('Assign at least one object to this customizable area before opening the Full Editor.', 'Admin') });
            return false;
        }
        const currentPrintableMeshIDs = new Set(
            useComposerStore
                .getState()
                .sceneObjectList.filter(isPrintableComposerMesh)
                .map((mesh) => mesh.id)
        );
        if (currentMeshIDs.some((meshID) => !currentPrintableMeshIDs.has(meshID))) {
            showWarningNotification({
                message: T._('This area is linked to an Empty or unavailable object. Select a mesh with geometry in Placement → Objects.', 'Admin')
            });
            return false;
        }
        // Persist product/area changes needed by the server-backed editor. Template-only changes
        // remain a Composer draft on reopening; they are hydrated into the editor below.
        const beforeSave = useComposerStore.getState();
        const existingTemplate = beforeSave.templates.find((candidate) => candidate.id === templateNodeID);
        const existingDesign = existingTemplate ? (beforeSave.getDesign(existingTemplate.id, area.id) ?? existingTemplate.design) : null;
        const existingDesignID = Number(existingDesign?.getDesignID?.() || existingTemplate?.templateID || 0);
        const hasPendingChanges = hasComposerAreaChangesRequiredByFullEditor(createComposerDirtySnapshot(beforeSave), composerSnapshotRef.current, area.id);
        // Earlier Composer builds persisted centimeter values as pixel bounds and an empty path.
        // Repair those sides before opening the editor, otherwise every placeholder is outside
        // the printable area even when the Composer has no visible pending changes.
        const areaPath = area.originalSide?.get('areas')?.at(0)?.get('path');
        const needsAreaRepair = !areaPath;
        if ((hasPendingChanges || !existingDesignID || needsAreaRepair) && !(await onSave(false))) return false;

        const latestStore = useComposerStore.getState();
        const template = latestStore.templates.find((candidate) => candidate.id === templateNodeID);
        const design = template ? (latestStore.getDesign(template.id, area.id) ?? template.design) : null;
        const designID = Number(design?.getDesignID?.() || template?.templateID || 0);
        const savedArea = latestStore.areas.find((candidate) => candidate.id === area.id);
        const meshIds = [...new Set((savedArea?.objects || '').split(';').filter(Boolean))];
        if (!state.product.id || !template || !designID) {
            showWarningNotification({
                message: T._('The template was saved, but the Full Editor could not be opened because its saved ID is not available.', 'Admin')
            });
            return false;
        }
        if (!savedArea || savedArea.sideID <= 0 || meshIds.length === 0) {
            showWarningNotification({ message: T._('Assign at least one object to this customizable area before opening the Full Editor.', 'Admin') });
            return false;
        }
        const printableMeshIDs = new Set(
            useComposerStore
                .getState()
                .sceneObjectList.filter(isPrintableComposerMesh)
                .map((mesh) => mesh.id)
        );
        if (meshIds.some((meshID) => !printableMeshIDs.has(meshID))) {
            showWarningNotification({
                message: T._('This area is linked to an Empty or unavailable object. Select a mesh with geometry in Placement → Objects.', 'Admin')
            });
            return false;
        }

        // Shared by live local snapshots and Apply, so a legacy multi-side item is forked only once.
        const draftItemIdentityMap = new Map<string, string>();
        const stageAreaDraft = (
            { design: areaDraft, sideId, updateSettings }: { design: unknown; sideId: number; updateSettings: boolean },
            closeEditor: boolean
        ) => {
            try {
                const currentStore = useComposerStore.getState();
                const currentTemplate = currentStore.templates.find((candidate) => candidate.id === template.id);
                if (!currentTemplate || !zkProduct || sideId !== savedArea.sideID) throw new Error('The edited area is no longer available.');
                const mergedDesign = mergeComposerTemplateAreaDraft(currentTemplate.design, areaDraft, sideId, updateSettings, draftItemIdentityMap);
                const mergedTemplate = ComposerTemplateNodeAdapter.toNode(
                    currentTemplate.templateID,
                    currentTemplate.name,
                    mergedDesign,
                    currentTemplate.id,
                    zkProduct,
                    zkProduct.get('colors').at(0)?.get('sides')
                );
                useComposerStore.setState((current) => {
                    const designs = new Map(current.designs);
                    for (const key of designs.keys()) if (key.startsWith(`${template.id},`)) designs.set(key, mergedTemplate.design);
                    return {
                        templates: current.templates.map((candidate) => (candidate.id === template.id ? mergedTemplate : candidate)),
                        selectedTemplates: current.selectedTemplates?.map((candidate) => (candidate.id === template.id ? mergedTemplate : candidate)) ?? null,
                        designs
                    };
                });
                if (closeEditor) {
                    setFullEditorDirty(false);
                    closeDialog('composer-full-template-editor');
                    showInfoNotification({
                        message: stringFormat(T._('Template changes are a draft. Save {0} to publish them.', 'Admin'), productLogicName())
                    });
                }
            } catch (error) {
                console.error('[Composer] template draft rejected', error);
                showWarningNotification({ message: error instanceof Error ? error.message : T._('The template draft could not be staged.', 'Admin') });
            }
        };

        showDialog(
            'composer-full-template-editor',
            <TemplateEditorDialog
                productId={state.product.id}
                designId={designID}
                categoryId={null}
                areaScope={{
                    sideId: savedArea.sideID,
                    meshIds,
                    cameraId: savedArea.camera,
                    restrictions: {
                        isTextEnabled:
                            template.restrictions.get(savedArea.sideID)?.userCanAddTexts ??
                            template.design
                                .get('sides')
                                .find((side) => side.get('sideID') === savedArea.sideID)
                                ?.get('canAddText') ??
                            true,
                        isImageEnabled:
                            template.restrictions.get(savedArea.sideID)?.userCanAddImages ??
                            template.design
                                .get('sides')
                                .find((side) => side.get('sideID') === savedArea.sideID)
                                ?.get('canAddImage') ??
                            true
                    }
                }}
                composerDraft={design?.toJSON()}
                onDirtyChange={setFullEditorDirty}
                onDraftError={(message) => showWarningNotification({ message })}
                onSnapshot={(draft) => stageAreaDraft(draft, false)}
                onDraft={(draft) => stageAreaDraft(draft, true)}
                onClose={() => {
                    setFullEditorDirty(false);
                    closeDialog('composer-full-template-editor');
                }}
                onSave={(savedDesignID) => {
                    closeDialog('composer-full-template-editor');
                    setTemplateRefreshTarget({ request: Date.now(), templateNodeID: template.id, designID: savedDesignID || designID });
                }}
            />
        );
        return true;
    };

    const openSettings = () => {
        const before = snapshotPreferences();
        const closeAndRestore = () => {
            restorePreferences(before);
            closeDialog('scene-editor-settings');
        };

        showDialog(
            'scene-editor-settings',
            <Preview3DStoreContext.Provider value={preview3DStore}>
                <Dialog
                    title={T._('Settings', 'Admin')}
                    size='large'
                    onClose={closeAndRestore}
                    buttons={[
                        { label: T._('Cancel', 'Admin'), outline: true, onClick: closeAndRestore },
                        { label: T._('Confirm', 'Admin'), onClick: () => closeDialog('scene-editor-settings') }
                    ]}
                >
                    <SceneEditorSettingsDialog
                        host='composer'
                        preferences={preferences}
                        setPreference={setPreference}
                        onResetPanelSizes={() => {
                            setStructureWidth(undefined);
                            resetPanelSizes();
                        }}
                        sceneEditor={sceneEditor}
                        zkScene={zkScene}
                        zkProduct={zkProduct}
                        additionalFeatures={additionalFeatures}
                        onSceneChanged={() => setHasUnsavedChanges(true)}
                    />
                </Dialog>
            </Preview3DStoreContext.Provider>
        );
    };

    if (saveError) {
        return (
            <Container>
                <ErrorPage
                    error={saveError}
                    title={stringFormat(T._('{0} could not be saved', 'Admin'), productLogicName())}
                    description={stringFormat(
                        T._(
                            'Your changes are still available in this page. You can return to {0} or try the save again. If the problem continues, reload the page and contact support.',
                            'Admin'
                        ),
                        productLogicName()
                    )}
                    primaryAction={{ label: T._('Try again', 'Admin'), onClick: () => void onSave() }}
                    secondaryAction={{ label: stringFormat(T._('Back to {0}', 'Admin'), productLogicName()), onClick: () => setSaveError(null) }}
                />
            </Container>
        );
    }

    const startStructureResize = (event: React.MouseEvent<HTMLDivElement>) => {
        const previousPanel = event.currentTarget.previousElementSibling as HTMLElement | null;
        if (!previousPanel) return;
        const startX = event.clientX;
        const startWidth = previousPanel.getBoundingClientRect().width;
        let nextWidth = startWidth;
        const move = (moveEvent: MouseEvent) => {
            nextWidth = Math.max(structureMinWidth, Math.min(structureMaxWidth, startWidth + moveEvent.clientX - startX));
            setStructureWidth(nextWidth);
        };
        const stop = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
            setPanelSize('left', nextWidth);
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', stop);
    };

    return (
        <Container>
            {templateRefreshTarget && (
                <CustomErrorBoundary key={templateRefreshTarget.request} render={() => <TemplateRefreshFailed onFailure={onTemplateRefreshFailed} />}>
                    <Suspense fallback={null}>
                        <RefreshedTemplate target={templateRefreshTarget} onLoaded={onRefreshedTemplate} />
                    </Suspense>
                </CustomErrorBoundary>
            )}
            <HeaderToolbar
                activeMode='composer'
                hasUnsavedChanges={hasUnsavedChanges || fullEditorDirty}
                isSaving={isSaving}
                localDraftState={localDraftState}
                onSave={onSave}
                onSettings={openSettings}
            />
            <MainContentContainer>
                <MainContentLayout ref={measureWorkspace} $structureWidth={resolvedStructureWidth}>
                    <Suspense fallback={<Loader />}>
                        <AttributesEditor />
                        <ComposerResizeHandle
                            aria-label={T._('Resize structure column', 'Admin')}
                            aria-orientation='vertical'
                            aria-valuemax={structureMaxWidth}
                            aria-valuemin={structureMinWidth}
                            aria-valuenow={Math.round(resolvedStructureWidth)}
                            onDoubleClick={() => {
                                setStructureWidth(structureMinWidth);
                                setPanelSize('left', structureMinWidth);
                            }}
                            onKeyDown={(event) => {
                                if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                                event.preventDefault();
                                const currentWidth =
                                    (event.currentTarget.previousElementSibling as HTMLElement | null)?.getBoundingClientRect().width ?? structureMinWidth;
                                const nextWidth = Math.max(
                                    structureMinWidth,
                                    Math.min(structureMaxWidth, currentWidth + (event.key === 'ArrowRight' ? 16 : -16))
                                );
                                setStructureWidth(nextWidth);
                                setPanelSize('left', nextWidth);
                            }}
                            onMouseDown={startStructureResize}
                            role='separator'
                            tabIndex={0}
                            title={T._('Drag to resize · double-click to reset', 'Admin')}
                        />
                        <PreviewContainer>
                            <ComposerAttributeContentPage
                                propertiesPanel={preferences.propertiesPanel}
                                onOpenFullEditor={openFullEditor}
                                onPreparePresetEditor={async (emptyPresetID) => {
                                    const before = useComposerStore.getState();
                                    if (
                                        emptyPresetID &&
                                        !fullEditorDirty &&
                                        !hasComposerChangesOutsidePreset(createComposerDirtySnapshot(before), composerSnapshotRef.current, emptyPresetID)
                                    )
                                        return true;
                                    const emptyDraft = before.attributes.find((node) => node.id === emptyPresetID && node.idNumeric <= 0);
                                    // The editor needs a saved product, but a new preset attribute cannot be
                                    // saved until its first composition exists. Keep that attribute in the
                                    // local draft while saving the rest of the product, then restore it.
                                    if (emptyDraft) useComposerStore.setState({ attributes: before.attributes.filter((node) => node.id !== emptyDraft.id) });
                                    try {
                                        const pendingChanges =
                                            createComposerDirtySnapshot(useComposerStore.getState()) !== composerSnapshotRef.current || fullEditorDirty;
                                        return pendingChanges ? await onSave(false) : true;
                                    } finally {
                                        if (emptyDraft) {
                                            useComposerStore.setState((current) => ({
                                                attributes: before.attributes.map((node) => current.attributes.find((saved) => saved.id === node.id) ?? node)
                                            }));
                                        }
                                    }
                                }}
                            />
                        </PreviewContainer>
                    </Suspense>
                </MainContentLayout>
            </MainContentContainer>
            {isSaving && (
                <EditorLoadingCard
                    title={stringFormat(T._('Saving {0}', 'Admin'), productLogicName())}
                    lines={[savingMessage, T._('Keep this page open until the operation is complete.', 'Admin')]}
                />
            )}
        </Container>
    );
};
export default Composer;
